/** Beneficiary confirmation landing page — Phase D of
 * CAMPAIGN_LEADS_QUESTIONS.md.
 *
 * Public, unauthenticated. The signed token in `?t=` is the only
 * auth factor. Three states:
 *   - Initial preview → "Confirm" + "Reschedule" buttons
 *   - Accept → "see you on [date]" thank-you
 *   - Reschedule → Calendly-style monthly slot picker
 *
 * Uses the marketing-site primitives (page-header, card, btn-primary)
 * so the beneficiary's first contact with NexBasira matches the
 * marketing site they may have visited.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime, formatTime } from "@/lib/format";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";


type PreviewPayload = {
  org_name: string;
  operator_name: string;
  scheduled_for: string | null;
  notes: string;
  session_status: string;
  already_used: boolean;
  used_action: "accept" | "reschedule" | null;
  /** Backlog 2026-09-22 — the beneficiary's own join link. Confirming
   *  used to be a dead end: the accepted screen said "see you on
   *  <date>" and offered no way into the call. */
  join_url?: string | null;
};


type SlotInfo = { starts_at: string; available: boolean; remaining?: number };
type AvailabilityPayload = {
  month: string;
  operator_count?: number | null;
  slots: SlotInfo[];
};


function fmtDateTime(iso: string | null): string {
  if (!iso) return "";
  return formatDateTime(iso, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


export default function ConfirmSessionPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";
  const initialAction = params.get("action") ?? "";
  const [mode, setMode] = useState<"preview" | "reschedule" | "accepted">(
    initialAction === "reschedule" ? "reschedule" : "preview",
  );
  const qc = useQueryClient();

  const preview = useQuery({
    queryKey: ["confirm-preview", sessionId, token],
    queryFn: async () => {
      const res = await api.get<PreviewPayload>(
        ApiPaths.confirmPreview(sessionId!),
        { params: { t: token } },
      );
      return res.data;
    },
    enabled: !!sessionId && !!token,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: async () => {
      const res = await api.post(ApiPaths.confirmAccept(sessionId!), { t: token });
      return res.data;
    },
    onSuccess: () => {
      setMode("accepted");
      void qc.invalidateQueries({
        queryKey: ["confirm-preview", sessionId, token],
      });
    },
  });

  if (preview.isLoading) {
    return (
      <ShellContainer>
        <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
      </ShellContainer>
    );
  }
  if (preview.isError || !preview.data) {
    return (
      <ShellContainer>
        <h1 className="page-title">{t("confirm.invalidTitle")}</h1>
        <p className="mt-2 text-sm text-nb-text-2">
          {describeApiError(preview.error, t("confirm.invalidBody"))}
        </p>
      </ShellContainer>
    );
  }

  if (preview.data.already_used && preview.data.used_action === "accept") {
    return (
      <AcceptedShell
        scheduled_for={preview.data.scheduled_for}
        join_url={preview.data.join_url}
      />
    );
  }

  if (mode === "accepted") {
    return (
      <AcceptedShell
        scheduled_for={preview.data.scheduled_for}
        join_url={preview.data.join_url}
      />
    );
  }

  // Blank booking (campaign launch wave): no pre-proposed time → go
  // straight to the slot picker in "book" mode, skipping the
  // confirm-proposed-time screen entirely (#10 Phase 2).
  if (preview.data.scheduled_for === null) {
    return (
      <ReschedulePicker
        isBooking
        sessionId={sessionId!}
        token={token}
        currentSlot={null}
        onCancel={() => {}}
        onRescheduled={(newSlot) => {
          qc.setQueryData<PreviewPayload>(
            ["confirm-preview", sessionId, token],
            (cur) => (cur ? { ...cur, scheduled_for: newSlot } : cur),
          );
          setMode("accepted");
        }}
      />
    );
  }

  if (mode === "reschedule") {
    return (
      <ReschedulePicker
        sessionId={sessionId!}
        token={token}
        currentSlot={preview.data.scheduled_for}
        onCancel={() => setMode("preview")}
        onRescheduled={(newSlot) => {
          // Push the new slot into the preview cache so the next view
          // shows the accepted screen with the right time.
          qc.setQueryData<PreviewPayload>(
            ["confirm-preview", sessionId, token],
            (cur) =>
              cur
                ? {
                    ...cur,
                    scheduled_for: newSlot,
                  }
                : cur,
          );
          setMode("accepted");
        }}
      />
    );
  }

  return (
    <ShellContainer>
      <div className="eyebrow">{t("confirm.eyebrow")}</div>
      <h1 className="page-title mt-2">{t("confirm.previewTitle")}</h1>
      <p className="mt-3 text-base text-nb-text-2">
        {t("confirm.previewBody", {
          operator: preview.data.operator_name,
          org: preview.data.org_name,
          when: fmtDateTime(preview.data.scheduled_for),
        })}
      </p>
      {preview.data.notes && (
        <p className="mt-3 rounded-md border-l-2 border-nb-mint bg-nb-bg-2 px-3 py-2 text-sm text-nb-text-2">
          {preview.data.notes}
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          onClick={() => accept.mutate()}
          disabled={accept.isPending}
        >
          {accept.isPending
            ? t("common.saving")
            : t("confirm.acceptButton")}
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setMode("reschedule")}
        >
          {t("confirm.rescheduleButton")} →
        </button>
      </div>
      {accept.isError && (
        <p className="mt-3 text-sm text-cvp-danger">
          {describeApiError(accept.error, t("confirm.acceptErrorGeneric"))}
        </p>
      )}
    </ShellContainer>
  );
}


function ShellContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-nb-bg text-nb-text">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="card">{children}</div>
      </div>
    </div>
  );
}


function AcceptedShell({
  scheduled_for,
  join_url,
}: {
  scheduled_for: string | null;
  join_url?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <ShellContainer>
      <div className="eyebrow text-cvp-ok">{t("confirm.acceptedEyebrow")}</div>
      <h1 className="page-title mt-2">{t("confirm.acceptedTitle")}</h1>
      <p className="mt-3 text-base text-nb-text-2">
        {t("confirm.acceptedBody", { when: fmtDateTime(scheduled_for) })}
      </p>
      {/* Backlog 2026-09-22 — reported: "I confirm the appointment but
          it does not allow me to join." Confirming was a dead end. The
          same link the reminder carries is offered here, so the
          beneficiary can go straight in (and bookmark this page to come
          back at the appointment time). */}
      {join_url && (
        <div className="mt-6">
          <a className="btn-primary inline-block" href={join_url}>
            {t("confirm.joinNow", "Join the inspection")}
          </a>
          <p className="mt-2 text-xs text-nb-text-3">
            {t(
              "confirm.joinNowHint",
              "Keep this link — use it to join when it's time.",
            )}
          </p>
        </div>
      )}
    </ShellContainer>
  );
}


function ReschedulePicker({
  sessionId,
  token,
  currentSlot,
  onCancel,
  onRescheduled,
  isBooking = false,
}: {
  sessionId: string;
  token: string;
  currentSlot: string | null;
  onCancel: () => void;
  onRescheduled: (newIso: string) => void;
  /** First-time booking (blank session) — "book" copy, no back-link. */
  isBooking?: boolean;
}) {
  const { t } = useTranslation();
  const [monthOffset, setMonthOffset] = useState(0);
  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [monthOffset]);

  const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}`;

  const slots = useQuery({
    queryKey: ["confirm-availability", sessionId, monthKey],
    queryFn: async () => {
      const res = await api.get<AvailabilityPayload>(
        ApiPaths.confirmAvailability(sessionId),
        { params: { t: token, month: monthKey } },
      );
      return res.data;
    },
  });

  const [picked, setPicked] = useState<string | null>(null);
  // Show only the next few days up front; the rest are behind "view more"
  // so the beneficiary isn't faced with a long wall of dates.
  const [showAllDays, setShowAllDays] = useState(false);

  const submit = useMutation({
    mutationFn: async (iso: string) => {
      const res = await api.post(ApiPaths.confirmReschedule(sessionId), {
        t: token,
        new_scheduled_for: iso,
      });
      return res.data;
    },
    onSuccess: (_, iso) => onRescheduled(iso),
  });

  // Group slots by day for the calendar layout.
  const slotsByDay = useMemo(() => {
    const map = new Map<string, SlotInfo[]>();
    for (const s of slots.data?.slots ?? []) {
      const day = s.starts_at.slice(0, 10);
      const arr = map.get(day) ?? [];
      arr.push(s);
      map.set(day, arr);
    }
    return map;
  }, [slots.data]);

  const days = Array.from(slotsByDay.entries()).filter(([, arr]) =>
    arr.some((s) => s.available),
  );
  const DEFAULT_DAYS = 3;
  const visibleDays = showAllDays ? days : days.slice(0, DEFAULT_DAYS);
  const hiddenDayCount = days.length - visibleDays.length;

  return (
    <ShellContainer>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="eyebrow">
            {isBooking
              ? t("confirm.bookEyebrow", "Inspection booking")
              : t("confirm.rescheduleEyebrow")}
          </div>
          <h1 className="page-title mt-2">
            {isBooking
              ? t("confirm.bookTitle", "Pick a time for your inspection")
              : t("confirm.rescheduleTitle")}
          </h1>
        </div>
        {!isBooking && (
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onCancel}
          >
            ← {t("common.back")}
          </button>
        )}
      </div>
      <p className="mt-2 text-sm text-nb-text-2">
        {isBooking
          ? t("confirm.bookBody", "Choose a slot that works for you below.")
          : t("confirm.rescheduleCurrentSlot", {
              when: fmtDateTime(currentSlot),
            })}
      </p>

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => {
            setMonthOffset((v) => v - 1);
            setPicked(null);
            setShowAllDays(false);
          }}
          disabled={monthOffset <= 0}
        >
          ← {t("confirm.previousMonth")}
        </button>
        <span className="font-semibold">
          {formatDate(monthStart, { month: "long", year: "numeric" })}
        </span>
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => {
            setMonthOffset((v) => v + 1);
            setPicked(null);
            setShowAllDays(false);
          }}
        >
          {t("confirm.nextMonth")} →
        </button>
      </div>

      {slots.isLoading && (
        <p className="mt-4 text-sm text-nb-text-3">{t("common.loading")}</p>
      )}
      {slots.data && days.length === 0 && (
        <p className="mt-4 text-sm text-nb-text-3">{t("confirm.noSlots")}</p>
      )}
      {days.length > 0 && (
        <div className="mt-4 space-y-3">
          {visibleDays.map(([day, daySlots]) => (
            <div key={day}>
              <h3 className="text-sm font-semibold text-nb-text">
                {formatDate(day + "T00:00:00Z", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </h3>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {daySlots
                  .filter((s) => s.available)
                  .map((s) => (
                    <button
                      key={s.starts_at}
                      type="button"
                      className={
                        "rounded-md border px-2.5 py-1 text-xs " +
                        (picked === s.starts_at
                          ? "border-nb-mint bg-nb-mint/20 text-nb-mint"
                          : "border-nb-border text-nb-text hover:border-nb-mint")
                      }
                      onClick={() => setPicked(s.starts_at)}
                      title={
                        typeof s.remaining === "number"
                          ? `${s.remaining} operator${s.remaining === 1 ? "" : "s"} free`
                          : undefined
                      }
                    >
                      {formatTime(s.starts_at, {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {typeof s.remaining === "number" && s.remaining > 1 && (
                        <span className="ml-1 text-[10px] text-nb-text-3">
                          ·{s.remaining}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            </div>
          ))}
          {hiddenDayCount > 0 && !showAllDays && (
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setShowAllDays(true)}
            >
              {t("confirm.viewMoreDays", {
                count: hiddenDayCount,
                defaultValue: "View more dates ({{count}} more day(s))",
              })}
            </button>
          )}
          {showAllDays && days.length > DEFAULT_DAYS && (
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setShowAllDays(false)}
            >
              {t("confirm.showFewerDays", "Show fewer dates")}
            </button>
          )}
        </div>
      )}

      {picked && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="text-sm text-nb-text-2">
            {t("confirm.pickedLabel", { when: fmtDateTime(picked) })}
          </span>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => submit.mutate(picked)}
            disabled={submit.isPending}
          >
            {submit.isPending
              ? t("common.saving")
              : isBooking
                ? t("confirm.bookConfirm", "Book this time")
                : t("confirm.confirmNewSlot")}
          </button>
        </div>
      )}
      {submit.isError && (
        <p className="mt-3 text-sm text-cvp-danger">
          {describeApiError(submit.error, t("confirm.rescheduleErrorGeneric"))}
        </p>
      )}
    </ShellContainer>
  );
}
